'use client';

import { Alert } from '@/components/common/feedback/alert/alert';
import { IconEye, IconEyeInvisible } from '@/components/common/icons/SVGs';
import { Button } from '@/components/common/inputs/button/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/common/inputs/text/form';
import { Input } from '@/components/common/inputs/text/input';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/common/layout/card';
import { ConfirmationDialog } from '@/components/features/core/auth/ConfirmationDialog';
import ForgotPasswordModal from '@/components/features/core/auth/components/ForgotPasswordModal';
import { CDN_BASE_URL } from '@/lib/constants/cdn';
import ROUTES from '@/lib/constants/routeConfig';
import { useMetrics, usePageViewTracking } from '@/lib/hooks/useMetrics';
import { MetricsAction, MetricsCategory } from '@/lib/utils/metrics.utils';
import { useI18n } from '@/locales/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { signIn } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSession } from 'next-auth/react';

export default function Login() {
  const t = useI18n();
  const router = useRouter();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [credentials, setCredentials] = useState<LoginFormSchemaType | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const { trackEvent } = useMetrics();

  const { data: session } = useSession();
  const userId = session?.user.id;
  usePageViewTracking('Login Page', userId, 'Login');

  const LoginFormSchema = z.object({
    email: z.string().email(t('register.errors.email')),
    password: z.string().min(8, t('register.errors.password')),
  });

  type LoginFormSchemaType = z.infer<typeof LoginFormSchema>;

  const form = useForm<LoginFormSchemaType>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function onSubmit(values: z.infer<typeof LoginFormSchema>) {
    try {
      setError(null);
      setIsLoading(true);
      setCredentials(values);

      // Track login attempt
      trackEvent({
        category: MetricsCategory.USER_ACTIVITY,
        action: MetricsAction.LOGIN,
        label: 'Login Attempt',
        value: 0,
        dimensions: { method: 'credentials' },
      });

      const result = await signIn('credentials', {
        email: values.email?.toLowerCase(),
        password: values.password,
        redirect: false,
        callbackUrl: '/',
      });

      if (result?.error === 'USER_NOT_CONFIRMED') {
        setShowConfirmation(true);
        // Track unconfirmed user login attempt
        trackEvent({
          category: MetricsCategory.USER_ACTIVITY,
          action: MetricsAction.LOGIN,
          label: 'Unconfirmed User',
          value: 0,
          dimensions: { email: values.email?.toLowerCase() },
        });
        return;
      }

      if (result?.error) {
        setError(t('auth.login.errors.generic'));
        // Track login error
        trackEvent({
          category: MetricsCategory.ERROR,
          action: MetricsAction.CLIENT_ERROR,
          label: 'Login Failed',
          value: 0,
          dimensions: { error: result.error },
        });
        return;
      }

      if (result?.ok) {
        // Track successful login
        trackEvent({
          category: MetricsCategory.USER_ACTIVITY,
          action: MetricsAction.LOGIN,
          label: 'Login Success',
          value: 0,
          dimensions: { method: 'credentials' },
        });
        await new Promise((resolve) => setTimeout(resolve, 100));
        window.location.replace('/');
      }
    } catch (e) {
      setError(t('auth.login.errors.generic'));
      // Track exception during login
      trackEvent({
        category: MetricsCategory.ERROR,
        action: MetricsAction.CLIENT_ERROR,
        label: 'Login Exception',
        value: 0,
        dimensions: { error: e instanceof Error ? e.message : 'Unknown error' },
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleConfirmationSuccess = async () => {
    setShowConfirmation(false);
    if (credentials) {
      await onSubmit(credentials);
    }
  };

  return (
    <>
      <Card className='mx-auto w-full max-w-[600px] border-none bg-transparent px-4 shadow-none sm:px-0'>
        <CardHeader className='space-y-2 text-center'>
          <div className='flex flex-col items-center'>
            <div className='relative h-[111px] w-[111px]'>
              <Image
                fill
                priority
                sizes='(max-width: 111px) 100vw, 111px'
                style={{ objectFit: 'cover' }}
                alt='Jaqln Logo'
                src={`${CDN_BASE_URL}/JAQLNLogo.png`}
              />
            </div>
            <CardTitle className='text-3xl font-bold'>
              {t('auth.title')}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className='mb-4 text-center text-sm text-red-500'>{error}</div>
          )}
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Form {...form}>
              <div className='space-y-4'>
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-base font-medium text-foreground'>
                        {t('register.email')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          autoComplete='email'
                          type='email'
                          className='h-12 rounded-lg focus:ring-2 focus:ring-primary'
                          data-testid='email-input'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className='text-xs' />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-base font-medium text-foreground'>
                        {t('register.password')}
                      </FormLabel>
                      <FormControl>
                        <div className='relative'>
                          <Input
                            autoComplete='current-password'
                            type={showPassword ? 'text' : 'password'}
                            className='h-12 rounded-lg pr-12 focus:ring-2 focus:ring-primary'
                            data-testid='password-input'
                            {...field}
                          />
                          <button
                            type='button'
                            onClick={() => setShowPassword(!showPassword)}
                            className='absolute right-3 top-1/2 -translate-y-1/2'
                            data-testid='toggle-password-visibility'
                          >
                            {showPassword ? (
                              <IconEyeInvisible
                                color='currentColor'
                                className='text-primary'
                                height={24}
                                width={24}
                              />
                            ) : (
                              <IconEye
                                color='currentColor'
                                className='text-primary'
                                height={24}
                                width={24}
                              />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className='text-xs' />
                    </FormItem>
                  )}
                />

                <div className='mb-2 w-full text-right'>
                  <a
                    href='#'
                    onClick={(e) => {
                      e.preventDefault();
                      setForgotPasswordOpen(true);
                    }}
                    className='text-sm text-primary hover:text-primary/90'
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </div>
                <Button
                  type='submit'
                  className='h-12 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90'
                  disabled={isLoading}
                  data-testid='login-button'
                >
                  {isLoading ? t('auth.login.loading') : t('auth.connect')}
                </Button>
              </div>
            </Form>
          </form>

          <div className='relative my-6'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-border'></div>
            </div>
            <div className='relative flex justify-center text-sm'>
              <span className='bg-[#F9FAFB] px-2 text-muted-foreground dark:bg-background'>
                {t('auth.or')}
              </span>
            </div>
          </div>

          <button
            className='flex h-12 w-full items-center justify-center space-x-2 rounded-lg border border-border bg-card px-6 text-base font-medium text-card-foreground transition duration-75 ease-in hover:bg-muted'
            onClick={() => {
              // Track Google login attempt
              trackEvent({
                category: MetricsCategory.USER_ACTIVITY,
                action: MetricsAction.LOGIN,
                label: 'Login Attempt',
                value: 0,
                dimensions: { method: 'google' },
              });

              signIn('google', {
                callbackUrl: `/`,
                popup: true,
              });
            }}
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z'
                fill='#FBC02D'
              />
              <path
                d='M3.15302 7.3455L6.43852 9.755C7.32752 7.554 9.48052 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15902 2 4.82802 4.1685 3.15302 7.3455Z'
                fill='#E53935'
              />
              <path
                d='M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5717 17.5742 13.3037 18.0011 12 18C9.39897 18 7.19047 16.3415 6.35847 14.027L3.09747 16.5395C4.75247 19.778 8.11347 22 12 22Z'
                fill='#4CAF50'
              />
              <path
                d='M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.785L18.7045 19.404C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z'
                fill='#1565C0'
              />
            </svg>
            <span>{t('auth.continueWithGoogle')}</span>
          </button>

          {authError && <Alert variant='destructive'>{authError}</Alert>}
        </CardContent>
        <CardFooter>
          <div className='flex w-full justify-between space-x-2'>
            <span className='text-muted-foreground'>
              {t('auth.signInMessage')}
            </span>
            <Link href={ROUTES.AUTH.REGISTER} className='text-purple-600'>
              {t('auth.register')}
            </Link>
          </div>
        </CardFooter>
      </Card>

      {showConfirmation && credentials && (
        <ConfirmationDialog
          open={showConfirmation}
          email={credentials.email}
          password={credentials.password}
          onClose={() => setShowConfirmation(false)}
          onSuccess={handleConfirmationSuccess}
        />
      )}

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        setOpen={setForgotPasswordOpen}
        onComplete={() => {
          // Reset the form or handle completion logic
          setError(null);
        }}
      />
    </>
  );
}
